import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import styles from './IntercomReportModal.styles';
import { Upload, Button } from 'antd';
import Papa from 'papaparse';
import { CSVLink } from 'react-csv';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getIsVisibleModal, closeModal } from '../../redux/store/ui/common';

const oldReportHeaders = [
  'Conversation ID',
  'Conversation URL',
  'Title',
  'Conversation status',
  'Conversation tags',
  'Created at',
  'Last updated at',
  'Last closed at',
  'Time Zone',
  'Inbound/Outbound',
  'Reopened',
  'Closed',
  'Conversation rating',
  'Conversation rating requested',
  'Rating updated at',
  'Conversation rating remark',
  'Companies (ID)',
  'Companies (name)',
  'Company tags',
  'Email',
  'Location',
  'Name',
  'Type',
  'Participated (ID)',
  'Participated (name)',
  'Participated (email)',
  'People tags',
  'User ID',
  'Assigned to (ID)',
  'Assigned to (name)',
  'Team assigned to (ID)',
  'Team assigned to (name)',
  'Closed by (ID)',
  'Closed by (name)',
  'Teammates participated',
  'Time to first reply (seconds)',
  'Time to last close (seconds)',
  'Teammate replies',
  'Teammate rated (ID)',
  'Teammate rated (name)',
  'Channel',
  'Source',
  'Created by',
  'Language',
  'Auto-translated',
  'External ID',
  'Fin AI Agent: Preview',
  'AI Tone of Voice',
  'AI Answer Length',
  'AI Pronoun Formality',
  'Workflow: Preview',
  'Fin AI Agent resolution state',
  'Customer reports',
  'Ticket category',
  'Last outbound call state',
  'Workspace phone number',
  'Merged',
  'Fin AI Agent: Action used in reply',
  'Copilot used',
  'Created by app package code',
];

const importedReportHeaders = [
  'Conversation ID',
  'Conversation started at (Europe/Berlin)',
  'Channel',
  'Chatbot replied',
  'Conversation priority',
  'Conversation source',
  'Conversation tag',
  'Conversation URL',
  'Current conversation state',
  'Last teammate rating',
  'Last teammate rating remark',
  'Number of reassignments',
  'Replies sent',
  'Replies to close a conversation',
  'Resolved on first contact',
  'Source URL',
  'Started by',
  'Ticket type',
  'Title',
  'Topics',
  'User participant IDs',
  'User participant names',
  'Conversation created at (Europe/Berlin)',
  'Conversation first closed at (Europe/Berlin)',
  'Conversation first replied at (Europe/Berlin)',
  'Conversation last closed at (Europe/Berlin)',
  'Conversation updated at (Europe/Berlin)',
  'Fin AI Agent rating updated at (Europe/Berlin)',
  'First assignment at (Europe/Berlin)',
  'Last assignment at (Europe/Berlin)',
  'Last conversation rating updated at (Europe/Berlin)',
  'Last teammate assigned first reply at (Europe/Berlin)',
  'Fin AI Agent deflected',
  'Fin AI Agent involved',
  'Fin AI Agent last sent answer',
  'Fin AI Agent rating',
  'Fin AI Agent rating remark',
  'Fin AI Agent resolution state',
  'Fin answer coverage',
  'Fin content referenced',
  'Guidance applied',
  'Last chatbot rated',
  'Last chatbot rating',
  'Last chatbot rating remark',
  'Last chatbot rating updated at (Europe/Berlin)',
  'Call types',
  'With calls',
  'Call participants',
  'First closed by teammate',
  'Last closed by',
  'Last closed by ID',
  'Last closed by teammate',
  'Last teammate rated',
  'Last teammate rated ID',
  'Teammate currently assigned',
  'Teammate currently assigned ID',
  'Teammate first replied',
  'Teammate replied to',
  'Team currently assigned',
  'Team currently assigned ID',
  'First response time (seconds)',
  'First response time excluding time spent in bot inbox (seconds)',
  'First response time excluding time spent in bot inbox, only within office hours (seconds)',
  'First response time, only within office hours (seconds)',
  'Handling time (seconds)',
  'Time from first assignment to close (seconds)',
  'Time from first assignment to close, within office hours (seconds)',
  'Time to assignment before first admin reply (seconds)',
  'Time to assignment before first admin reply, within office hours (seconds)',
  'Time to close (seconds)',
  'Time to close excluding bot inbox time (seconds)',
  'Time to close excluding bot inbox time, within office hours (seconds)',
  'Time to close, within office hours (seconds)',
  'Time to first close (seconds)',
  'Time to first close excluding bot inbox time (seconds)',
  'Time to first close excluding bot inbox time, within office hours (seconds)',
  'Time to first close, within office hours (seconds)',
  'Copilot used',
  'Customer reports',
  'Ticket category',
  'AI Answer Length',
  'AI Pronoun Formality',
  'AI Tone of Voice',
  'Auto-translated',
  'Created by app package code',
  'External ID',
  'Fin AI Agent resolution state',
  'Fin AI Agent: Action used in reply',
  'Fin AI Agent: Preview',
  'Language',
  'Last outbound call state',
  'Merged',
  'Workflow: Preview',
  'Workspace phone number',
  'Company ID',
  'Company name',
  'Company plan',
  'Company size',
  'Company tag',
  'Continent',
  'Country',
  'User email',
  'User ID',
  'User name',
  'User pseudonym',
  'User tag',
  'User type',
  'Replying from WhatsApp',
];

type PhoneReportModalProps = {
  visible?: boolean;
};

const PhoneReportModal: React.FC<PhoneReportModalProps> = ({ visible }) => {
  const dispatch = useDispatch();
  const _visible = useSelector(getIsVisibleModal('intercomReportModal'));
  const [key, setKey] = React.useState(new Date().toString());
  const [intercomRow, setIntercomRow] = React.useState(null) as any;
  const [totalRows, setTotalRows] = React.useState([oldReportHeaders]);

  const handleCancel = () => {
    dispatch(closeModal('intercomReportModal'));
  };

  useEffect(() => {
    setKey(new Date().toString());
    setIntercomRow(null);
    setTotalRows([oldReportHeaders]);
  }, [_visible]);

  const intercomProps: any = {
    name: 'file',
    accept: '.csv',
    maxCount: 1,
    beforeUpload: (file) => {
      Papa.parse(file, {
        complete: (results) => {
          setIntercomRow(results);
        },
        error(error) {
          if (error) {
            setIntercomRow(null);
          }
        },
      });
      return false;
    },
    onRemove: () => {
      setIntercomRow(null);
    },
  };

  const getIndexOf = (
    item: { [x: string]: any },
    titles: string | any[],
    title: string | string[],
    mapper?: (value: any) => any
  ) => {
    const candidates = Array.isArray(title) ? title : [title];
    const index =
      candidates
        .map((candidate) => titles?.indexOf(candidate) ?? -1)
        .find((i) => i >= 0) ?? -1;
    if (index >= 0) {
      const value = item[index];
      if (value) {
        return mapper ? mapper(value) : value;
      }
    }
    return '';
  };

  const onDownloadPress = (event: any, done: () => void) => {
    let intercom: any = [];
    const headers = intercomRow?.data?.[0];

    if (intercomRow?.data?.length > 0) {
      intercomRow?.data?.forEach((item: { [x: string]: any }, i: number) => {
        if (i > 0) {
          let reopened = 0;
          let closed = 0;
          let teammateReplies = 0;
          if (
            getIndexOf(item, headers, 'Time to first close (seconds)') != ''
          ) {
            closed = 1;

            if (
              getIndexOf(item, headers, 'Time to first close (seconds)') !=
              getIndexOf(item, headers, 'Time to close (seconds)')
            ) {
              reopened = 1;
              closed = 2;
            }
          }

          if (getIndexOf(item, headers, 'First closed by teammate') != '') {
            // Intercom renamed this column: 'Replies sent' -> 'Teammate Replies'.
            // Accept both so archived pre-rename exports still parse.
            teammateReplies = getIndexOf(
              item,
              headers,
              ['Teammate Replies', 'Replies sent'],
              (v) => parseInt(v)
            );
          }

          const row = [];
          row.push(getIndexOf(item, headers, 'Conversation ID'));
          row.push(getIndexOf(item, headers, 'Conversation URL'));
          row.push(getIndexOf(item, headers, 'Title')); // date
          row.push(getIndexOf(item, headers, 'Current conversation state'));
          row.push(getIndexOf(item, headers, 'Conversation tag'));
          row.push(
            getIndexOf(item, headers, 'Conversation started at (Europe/Berlin)')
          );
          row.push(
            getIndexOf(item, headers, 'Conversation updated at (Europe/Berlin)')
          );
          row.push(
            getIndexOf(
              item,
              headers,
              'Conversation last closed at (Europe/Berlin)'
            )
          );
          row.push('Berlin');
          if (item['source'] === 'Manual message') {
            row.push('Outbound');
          } else {
            row.push('Inbound');
          }
          row.push(reopened);
          row.push(closed);
          row.push(getIndexOf(item, headers, 'Last teammate rating'));
          row.push('');
          row.push(
            getIndexOf(
              item,
              headers,
              'Last conversation rating updated at (Europe/Berlin)'
            )
          );
          row.push(getIndexOf(item, headers, 'Last teammate rating remark'));

          row.push(getIndexOf(item, headers, 'Company ID'));
          row.push(getIndexOf(item, headers, 'Company name'));
          row.push(getIndexOf(item, headers, 'Company tag'));

          row.push(getIndexOf(item, headers, 'User email'));
          row.push(getIndexOf(item, headers, 'Country'));
          // Anonymous leads have no 'User name' — Intercom gives them a
          // generated 'User pseudonym' instead, so fall back to that.
          row.push(
            getIndexOf(item, headers, 'User name') ||
              getIndexOf(item, headers, 'User pseudonym')
          );
          row.push(getIndexOf(item, headers, 'User type'));
          row.push(getIndexOf(item, headers, 'User ID'));
          row.push('');
          row.push('');
          row.push(getIndexOf(item, headers, 'User tag'));
          row.push(getIndexOf(item, headers, 'User ID'));
          row.push(getIndexOf(item, headers, 'Teammate currently assigned ID'));
          row.push(getIndexOf(item, headers, 'Teammate currently assigned'));
          row.push(getIndexOf(item, headers, 'Team currently assigned ID'));
          row.push(getIndexOf(item, headers, 'Team currently assigned'));
          row.push(getIndexOf(item, headers, 'Last closed by ID'));
          row.push(getIndexOf(item, headers, 'Last closed by teammate'));
          row.push(getIndexOf(item, headers, 'Teammate replied to'));
          row.push(
            getIndexOf(item, headers, 'First response time (seconds)', (v) =>
              v ? parseInt(v) : v
            )
          );
          row.push(
            getIndexOf(item, headers, 'Time to close (seconds)', (v) =>
              v ? parseInt(v) : v
            )
          );

          row.push(teammateReplies);
          row.push(getIndexOf(item, headers, 'Last teammate rated ID'));
          row.push(getIndexOf(item, headers, 'Last teammate rated'));
          row.push(getIndexOf(item, headers, 'Channel'));
          row.push(getIndexOf(item, headers, 'Conversation source'));
          row.push(getIndexOf(item, headers, 'Started by'));
          row.push(getIndexOf(item, headers, 'Language'));
          row.push(getIndexOf(item, headers, 'Auto-translated'));
          row.push(getIndexOf(item, headers, 'External ID'));
          row.push('');
          row.push(getIndexOf(item, headers, 'AI Tone of Voice'));
          row.push(getIndexOf(item, headers, 'AI Answer Length'));
          row.push(getIndexOf(item, headers, 'AI Pronoun Formality'));
          row.push(getIndexOf(item, headers, 'Workflow: Preview'));
          row.push(getIndexOf(item, headers, 'Fin AI Agent resolution state'));
          row.push(getIndexOf(item, headers, 'Customer reports'));
          row.push(getIndexOf(item, headers, 'Ticket category'));
          row.push(getIndexOf(item, headers, 'Last outbound call state'));
          row.push(getIndexOf(item, headers, 'Workspace phone number'));
          row.push(getIndexOf(item, headers, 'Merged'));
          row.push(
            getIndexOf(item, headers, 'Fin AI Agent: Action used in reply')
          );
          row.push(getIndexOf(item, headers, 'Fin AI Agent involved'));
          row.push(getIndexOf(item, headers, 'Created by app package code'));
          intercom.push(row);
        }
      });
    }

    setTotalRows([...totalRows, ...intercom]);
    done();
  };

  return (
    <>
      <div>
        <Modal
          wrapClassName="add-service-wrapper"
          title={`Intercom report`}
          visible={visible || _visible}
          width={720}
          onCancel={handleCancel}
          footer={null}
        >
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <Upload key={`${key}-airCallRow`} {...intercomProps}>
              <Button icon={<UploadOutlined />}>Upload Intercom Report</Button>
            </Upload>
          </div>

          <Button
            disabled={!intercomRow}
            type="primary"
            icon={<DownloadOutlined />}
          >
            <CSVLink
              className={
                !intercomRow ? 'disabled-download-btn' : 'download-btn'
              }
              filename="old-intercom-report.csv"
              data={totalRows}
              asyncOnClick
              onClick={onDownloadPress}
            >
              Update and download report
            </CSVLink>
          </Button>
        </Modal>
      </div>
      <style jsx global>
        {styles}
      </style>
    </>
  );
};

export default PhoneReportModal;
